import { ref, reactive, computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

import {
  generateCustomAttributeTypes,
  getDefaultConditions,
  getDefaultActions,
  generateCustomAttributes,
} from 'dashboard/helper/automationHelper';
import useAutomationValues from './useAutomationValues';

import {
  // AUTOMATION_RULE_EVENTS,
  // AUTOMATION_ACTION_TYPES,
  AUTOMATIONS,
  AUTOMATION_INBOX_ONLY_CONDITION_EVENT_KEYS,
} from 'dashboard/routes/dashboard/settings/automation/constants.js';
import {
  createWorkflowStepId,
  getDefaultDateTimeWindowParams,
  getDateTimeWindowBranchTargetId,
  setDateTimeWindowBranchTargetId,
} from 'dashboard/routes/dashboard/settings/automation/dateTimeWindow';

/**
 * Composable for handling automation-related functionality.
 * @returns {Object} An object containing various automation-related functions and computed properties.
 */
export function useAutomation(startValue = null) {
  const getters = useStoreGetters();
  const { t } = useI18n();
  const { currentAccount } = useAccount();

  const {
    booleanFilterOptions,
    statusFilterOptions,
    getConditionDropdownValues,
    getActionDropdownValues,
    agents,
    campaigns,
    contacts,
    inboxes,
    labels,
    teams,
    slaPolicies,
  } = useAutomationValues();

  const automation = ref(startValue);
  const automationTypes = reactive(structuredClone(AUTOMATIONS));
  const eventName = computed(() => automation.value?.event_name);
  const defaultTimezone = computed(
    () => currentAccount.value?.settings?.account_timezone || 'UTC'
  );

  const buildDefaultAction = actionName => {
    const baseAction = {
      id: createWorkflowStepId(),
      action_name: actionName,
      action_params: [],
      next_step_id: null,
    };

    if (actionName === 'date_time_window') {
      return {
        ...baseAction,
        action_params: getDefaultDateTimeWindowParams(defaultTimezone.value),
      };
    }

    return baseAction;
  };

  /**
   * Handles the event change for an automation.value.
   */
  const onEventChange = () => {
    automation.value.conditions = getDefaultConditions(eventName.value);
    automation.value.actions = [];
  };

  /**
   * Appends a new condition to the automation.value.
   */
  const appendNewCondition = () => {
    const defaultCondition = getDefaultConditions(eventName.value);
    automation.value.conditions = [
      ...automation.value.conditions,
      ...defaultCondition,
    ];
  };

  /**
   * Appends a new action to the automation.value.
   */
  const appendNewAction = () => {
    const defaultAction = getDefaultActions().map(action => ({
      ...action,
      id: createWorkflowStepId(),
      next_step_id: null,
    }));
    automation.value.actions = [...automation.value.actions, ...defaultAction];
  };

  /**
   * Inserts default action(s) immediately after `afterIndex` (-1 = before first action).
   */
  const insertActionAfter = (
    afterIndex,
    actionName = 'assign_agent',
    options = {}
  ) => {
    const chunk = [buildDefaultAction(actionName)];
    const next = [...automation.value.actions];
    const pos = afterIndex + 1;
    if (afterIndex >= 0) {
      const previousStep = next[afterIndex];
      if (
        previousStep?.action_name === 'date_time_window' &&
        options.branchKey
      ) {
        chunk[0].next_step_id = getDateTimeWindowBranchTargetId(
          previousStep.action_params,
          options.branchKey
        );
        previousStep.action_params = setDateTimeWindowBranchTargetId(
          previousStep.action_params,
          options.branchKey,
          chunk[0].id
        );
      } else if (previousStep) {
        if (actionName === 'date_time_window') {
          chunk[0].action_params = setDateTimeWindowBranchTargetId(
            chunk[0].action_params,
            'success',
            previousStep.next_step_id || null
          );
        } else {
          chunk[0].next_step_id = previousStep.next_step_id || null;
        }
        previousStep.next_step_id = chunk[0].id;
      }
    }
    next.splice(pos, 0, ...chunk);
    automation.value.actions = next;
  };

  /**
   * Removes a filter from the automation.value.
   * @param {number} index - The index of the filter to remove.
   */
  const removeFilter = index => {
    if (automation.value.conditions.length <= 1) {
      useAlert(t('AUTOMATION.CONDITION.DELETE_MESSAGE'));
    } else {
      automation.value.conditions = automation.value.conditions.filter(
        (_, i) => i !== index
      );
    }
  };

  /**
   * Removes an action from the automation.value.
   * @param {number} index - The index of the action to remove.
   */
  const removeAction = index => {
    if (automation.value.actions.length <= 1) {
      useAlert(t('AUTOMATION.ACTION.DELETE_MESSAGE'));
    } else {
      automation.value.actions = automation.value.actions.filter(
        (_, i) => i !== index
      );
    }
  };

  /**
   * Resets a filter in the automation.value.
   * @param {Object} automationTypes - The automation types object.
   * @param {number} index - The index of the filter to reset.
   * @param {Object} currentCondition - The current condition object.
   */
  const resetFilter = (index, currentCondition) => {
    const newConditions = [...automation.value.conditions];

    newConditions[index] = {
      ...newConditions[index],
      filter_operator: automationTypes[eventName.value].conditions.find(
        condition => condition.key === currentCondition.attribute_key
      ).filterOperators[0].value,
      values: '',
    };

    automation.value.conditions = newConditions;
  };

  /**
   * Resets an action in the automation.value.
   * @param {number} index - The index of the action to reset.
   */
  const resetAction = index => {
    const newActions = [...automation.value.actions];
    newActions[index] = {
      ...newActions[index],
      action_params:
        newActions[index].action_name === 'date_time_window'
          ? getDefaultDateTimeWindowParams(defaultTimezone.value)
          : [],
    };

    automation.value.actions = newActions;
  };

  /**
   * This function formats the custom attributes for automation types.
   * It retrieves custom attributes for conversations and contacts,
   * generates custom attribute types, and adds them to the relevant automation types.
   */
  const manifestCustomAttributes = () => {
    const conversationCustomAttributesRaw = getters[
      'attributes/getAttributesByModel'
    ].value('conversation_attribute');
    const contactCustomAttributesRaw =
      getters['attributes/getAttributesByModel'].value('contact_attribute');

    const conversationCustomAttributeTypes = generateCustomAttributeTypes(
      conversationCustomAttributesRaw,
      'conversation_attribute'
    );
    const contactCustomAttributeTypes = generateCustomAttributeTypes(
      contactCustomAttributesRaw,
      'contact_attribute'
    );

    const manifestedCustomAttributes = generateCustomAttributes(
      conversationCustomAttributeTypes,
      contactCustomAttributeTypes,
      t('AUTOMATION.CONDITION.CONVERSATION_CUSTOM_ATTR_LABEL'),
      t('AUTOMATION.CONDITION.CONTACT_CUSTOM_ATTR_LABEL')
    );

    const CUSTOM_ATTR_HEADER_KEYS = new Set([
      'conversation_custom_attribute',
      'contact_custom_attribute',
    ]);

    Object.keys(automationTypes).forEach(eventToUpdate => {
      const evt = automationTypes[eventToUpdate];
      if (!evt?.conditions) {
        return;
      }
      if (AUTOMATION_INBOX_ONLY_CONDITION_EVENT_KEYS.has(eventToUpdate)) {
        return;
      }
      const standardConditions = evt.conditions.filter(
        c => !c.customAttributeType && !CUSTOM_ATTR_HEADER_KEYS.has(c.key)
      );
      automationTypes[eventToUpdate].conditions = [
        ...standardConditions,
        ...manifestedCustomAttributes,
      ];
    });
  };

  return {
    automation,
    automationTypes,
    agents,
    campaigns,
    contacts,
    inboxes,
    labels,
    teams,
    slaPolicies,
    booleanFilterOptions,
    statusFilterOptions,
    onEventChange,
    getConditionDropdownValues,
    appendNewCondition,
    appendNewAction,
    insertActionAfter,
    removeFilter,
    removeAction,
    resetFilter,
    resetAction,
    getActionDropdownValues,
    manifestCustomAttributes,
  };
}
